import type {
  FriendsResponse,
  HintResponse,
  LevelCheckResponse,
  LeaderboardResponse,
  LevelFinishResponse,
  LevelStartResponse,
  MapResponse,
  PlayerProfile,
} from "@monekineko/shared";
import { initData, launchInfo, startParam, tg } from "./telegram.js";

// Клиент API. Токен живёт в памяти вкладки: обновляется по refresh-cookie,
// в localStorage ничего не кладём — мини-приложение и так открывается заново.

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

let token: string | null = null;
let refreshing: Promise<void> | null = null;

async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    credentials: "include",
    headers: {
      // content-type только при наличии тела: Fastify отвергает пустой JSON
      ...(init.body ? { "content-type": "application/json" } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (response.status === 401 && retry) {
    await refreshToken();
    return request<T>(path, init, false);
  }

  const text = await response.text();
  const data = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const error = (data as { error?: { code: string; message: string; details?: Record<string, unknown> } })?.error;
    throw new ApiError(
      error?.code ?? "http_error",
      error?.message ?? `Ошибка ${response.status}`,
      response.status,
      error?.details,
    );
  }

  return data as T;
}

async function refreshToken(): Promise<void> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const data = await request<{ token: string }>("/auth/refresh", { method: "POST" }, false);
        token = data.token;
      } catch {
        // refresh протух — заходим заново по initData
        await login();
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

export async function login(): Promise<PlayerProfile> {
  const data = await request<{ token: string; profile: PlayerProfile }>(
    "/auth/telegram",
    {
      method: "POST",
      body: JSON.stringify({
        initData: initData(),
        startParam: startParam(),
        launch: launchInfo(),
      }),
    },
    false,
  );
  token = data.token;
  return data.profile;
}

export const api = {
  me: () => request<PlayerProfile>("/me"),
  map: () => request<MapResponse>("/map"),

  startLevel: (levelId: string) =>
    request<LevelStartResponse>(`/levels/${levelId}/start`, { method: "POST" }),

  introSeen: (levelId: string) =>
    request<{ ok: boolean }>(`/levels/${levelId}/intro-seen`, { method: "POST" }),

  check: (instanceId: string, solution: unknown) =>
    request<LevelCheckResponse>(`/instances/${instanceId}/check`, {
      method: "POST",
      body: JSON.stringify({ solution }),
    }),

  finish: (instanceId: string, solution: unknown, clientMistakes?: number) =>
    request<LevelFinishResponse>(`/instances/${instanceId}/finish`, {
      method: "POST",
      body: JSON.stringify({ solution, clientMistakes }),
    }),

  hint: (instanceId: string) => request<HintResponse>(`/instances/${instanceId}/hint`, { method: "POST" }),

  abandon: (instanceId: string) =>
    request<{ ok: boolean }>(`/instances/${instanceId}/abandon`, { method: "POST" }),

  events: (instanceId: string, events: { type: string; payload?: Record<string, unknown> }[]) =>
    request<{ ok: boolean }>(`/instances/${instanceId}/events`, {
      method: "POST",
      body: JSON.stringify({ events }),
    }),

  ping: (sessionId?: string) =>
    request<{ sessionId: string; totalPlayMs: number }>("/session/ping", {
      method: "POST",
      body: JSON.stringify({ sessionId, platform: tg()?.platform ?? "web" }),
    }),

  leaderboard: (period: "week" | "all") =>
    request<LeaderboardResponse>(`/leaderboard?period=${period}`),

  friends: () => request<FriendsResponse>("/friends"),

  support: (text: string) =>
    request<{ ok: boolean }>("/support", { method: "POST", body: JSON.stringify({ text }) }),
};

/** Телеметрия копится и уходит пачкой: на каждый клик запрос слать незачем */
export function createEventQueue(instanceId: string) {
  let queue: { type: string; payload?: Record<string, unknown> }[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;

  const flush = async (): Promise<void> => {
    if (!queue.length) return;
    const batch = queue;
    queue = [];
    try {
      await api.events(instanceId, batch);
    } catch {
      // телеметрия не критична: потерялась — и ладно
    }
  };

  return {
    push(type: string, payload?: Record<string, unknown>) {
      queue.push({ type, payload });
      if (!timer) timer = setTimeout(() => {
        timer = null;
        void flush();
      }, 3000);
    },
    flush,
  };
}
